import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Crown, CheckCircle, ArrowRight, SpinnerGap, CreditCard } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Inline upgrade prompt shown on locked pages
export const UpgradePrompt = ({ feature, requiredTier = 'standard', pricing = {} }) => {
  const navigate = useNavigate();
  const [upgrading, setUpgrading] = useState(false);

  const tierLabels = {
    standard: 'Core',
    premium: 'Pass Faster',
  };

  const featureLabels = {
    mock_exam: 'Mock Exams',
    smart_review: 'Smart Review',
    hazard_perception: 'Hazard Perception',
    bookmarks: 'Bookmarks',
    ai_explanations: 'AI Explanations',
  };

  const handleUpgrade = async (tier) => {
    setUpgrading(true);
    try {
      const { data } = await axios.post(`${API}/subscription/checkout`, {
        tier,
        origin_url: window.location.origin,
      }, { withCredentials: true });
      window.location.href = data.url;
    } catch (error) {
      console.error('Failed to start checkout:', error);
      setUpgrading(false);
    }
  };

  const price = pricing[requiredTier] || (requiredTier === 'standard' ? 4.99 : 9.99);

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-4" data-testid="upgrade-prompt">
      <div className="max-w-md w-full text-center">
        <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-accent/10 flex items-center justify-center">
          <Lock weight="fill" className="w-8 h-8 text-accent" />
        </div>
        <h2 className="font-heading font-black text-2xl tracking-tighter mb-2">
          {featureLabels[feature] || feature} is Locked
        </h2>
        <p className="text-muted-foreground mb-6">
          Upgrade to {tierLabels[requiredTier] || 'Standard'} or higher to unlock this feature.
        </p>
        <Button
          onClick={() => handleUpgrade(requiredTier)}
          disabled={upgrading}
          className="rounded-sm w-full max-w-xs mx-auto"
          data-testid="upgrade-now-button"
        >
          {upgrading ? (
            <SpinnerGap className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <Crown weight="fill" className="w-5 h-5 mr-2" />
              Upgrade to {tierLabels[requiredTier]} — £{price}/mo
            </>
          )}
        </Button>
        <button
          onClick={() => navigate('/subscription')}
          className="mt-4 text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mx-auto"
          data-testid="view-all-plans"
        >
          Compare all plans <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// Lock overlay badge for category/feature cards
export const LockBadge = ({ className }) => (
  <div className={cn("absolute top-2 right-2 z-10", className)}>
    <div className="flex items-center gap-1.5 bg-card border border-border px-2.5 py-1 rounded-sm shadow-sm">
      <Lock weight="fill" className="w-3.5 h-3.5 text-accent" />
      <span className="text-[10px] font-bold text-accent uppercase tracking-wider">Locked</span>
    </div>
  </div>
);

// Tier badge shown next to user info
export const TierBadge = ({ tier, size = 'sm' }) => {
  const styles = {
    free: 'bg-muted text-muted-foreground',
    standard: 'bg-blue-500/20 text-blue-400',
    premium: 'bg-accent/20 text-accent',
  };

  const labels = { free: 'FREE', standard: 'CORE', premium: 'PASS FASTER' };

  return (
    <span
      className={cn(
        'font-bold uppercase tracking-wider rounded-sm inline-flex items-center gap-1',
        styles[tier] || styles.free,
        size === 'sm' ? 'px-2 py-0.5 text-[10px]' : 'px-3 py-1 text-xs'
      )}
      data-testid="tier-badge"
    >
      {tier === 'premium' && <Crown weight="fill" className="w-3 h-3" />}
      {labels[tier] || 'FREE'}
    </span>
  );
};

// Pricing comparison cards for settings page
export const PricingCards = ({ currentTier, pricing = {}, tiers = {} }) => {
  const [upgrading, setUpgrading] = useState(null);

  const handleUpgrade = async (tier) => {
    setUpgrading(tier);
    try {
      const { data } = await axios.post(`${API}/subscription/checkout`, {
        tier,
        origin_url: window.location.origin,
      }, { withCredentials: true });
      window.location.href = data.url;
    } catch (error) {
      console.error('Failed to start checkout:', error);
      setUpgrading(null);
    }
  };

  const tierOrder = ['free', 'standard', 'premium'];
  const currentIndex = tierOrder.indexOf(currentTier);

  const featureList = [
    { key: 'all_categories', label: 'All Question Categories', freeLabel: 'Limited Categories' },
    { key: 'mock_exam', label: 'Mock Exams' },
    { key: 'smart_review', label: 'Smart Review (Spaced Repetition)' },
    { key: 'bookmarks', label: 'Question Bookmarks' },
    { key: 'hazard_perception', label: 'Hazard Perception Practice' },
    { key: 'ai_explanations', label: 'AI-Powered Explanations' },
  ];

  const tierStyles = {
    free: { border: 'border-border', bg: 'bg-card', accent: 'text-muted-foreground' },
    standard: { border: 'border-blue-500/40', bg: 'bg-card', accent: 'text-blue-400' },
    premium: { border: 'border-accent/40', bg: 'bg-card', accent: 'text-accent' },
  };

  const tierNames = { free: 'Free', standard: 'Core', premium: 'Pass Faster' };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="pricing-cards">
      {tierOrder.map((tierKey, idx) => {
        const style = tierStyles[tierKey];
        const tierConfig = tiers?.[tierKey] || {};
        const price = pricing[tierKey] || 0;
        const isCurrent = tierKey === currentTier;
        const isUpgrade = idx > currentIndex;

        return (
          <div
            key={tierKey}
            className={cn(
              'rounded-sm border-2 p-5 relative',
              style.border,
              style.bg,
              tierKey === 'premium' && 'ring-1 ring-accent/20'
            )}
            data-testid={`pricing-card-${tierKey}`}
          >
            {tierKey === 'premium' && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent text-accent-foreground text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-sm">
                Most Popular
              </div>
            )}

            <div className="text-center mb-4">
              <h3 className={cn('font-heading font-bold text-lg', style.accent)}>
                {tierNames[tierKey]}
              </h3>
              <div className="mt-2">
                <span className="font-heading font-black text-3xl tracking-tight">
                  {price === 0 ? 'Free' : `£${price}`}
                </span>
                {price > 0 && (
                  <span className="text-sm text-muted-foreground">/mo</span>
                )}
              </div>
            </div>

            <ul className="space-y-2.5 mb-6">
              {featureList.map((f) => {
                const has = f.key === 'all_categories'
                  ? tierConfig.all_categories
                  : tierConfig[f.key];
                return (
                  <li key={f.key} className="flex items-start gap-2 text-sm">
                    {has ? (
                      <CheckCircle weight="fill" className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />
                    ) : (
                      <Lock weight="regular" className="w-4 h-4 text-muted-foreground/40 flex-shrink-0 mt-0.5" />
                    )}
                    <span className={cn(!has && 'text-muted-foreground/50 line-through')}>
                      {!has && f.freeLabel ? f.freeLabel : f.label}
                    </span>
                  </li>
                );
              })}
            </ul>

            {isCurrent ? (
              <div className="text-center py-2 text-sm font-bold text-muted-foreground border border-border rounded-sm" data-testid={`current-plan-${tierKey}`}>
                Current Plan
              </div>
            ) : isUpgrade ? (
              <Button
                onClick={() => handleUpgrade(tierKey)}
                disabled={upgrading === tierKey}
                className={cn(
                  'w-full rounded-sm',
                  tierKey === 'premium' && 'bg-accent hover:bg-accent/90 text-accent-foreground'
                )}
                data-testid={`upgrade-to-${tierKey}`}
              >
                {upgrading === tierKey ? (
                  <SpinnerGap className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <CreditCard weight="fill" className="w-4 h-4 mr-2" />
                    Upgrade — £{price}/mo
                  </>
                )}
              </Button>
            ) : (
              <div className="text-center py-2 text-xs text-muted-foreground/50">
                —
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
